import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, GraduationCap, Lock as LockIcon } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

export default function MyCourses() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/academy/my/courses').then(({ data }) => setRows(data.rows || [])),
      api.get('/api/academy/programs')
        .then(({ data }) => setAllPrograms(data.programs || data.rows || data || []))
        .catch(() => setAllPrograms([])),
    ]).finally(() => setLoading(false));
  }, [api]);

  const enrolledSlugs = new Set(rows.map((r) => r.program_slug));
  const otherPrograms = allPrograms.filter((p) => !enrolledSlugs.has(p.slug));

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">My Courses</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Programs you're enrolled in, plus the full catalog to browse.</p>

        {loading ? (
          <div className="text-[#6E6E73] text-[14px]">Loading…</div>
        ) : (
          <>
            {/* Enrolled — full access */}
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-[#c9a227]" />
                <h2 className="text-[15px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">Enrolled programs</h2>
                <span className="text-[11px] text-[#6E6E73]">{rows.length} active</span>
              </div>
              {rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-8 text-center">
                  <BookOpen className="w-9 h-9 text-[#c9a227] mx-auto mb-2" />
                  <div className="text-[15px] font-bold text-[#1D1D1F] mb-1">No active enrolments</div>
                  <p className="text-[13px] text-[#6E6E73]">Pick a program below to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rows.map((c) => (
                    <Link key={c.program_slug} to={`/academy/courses/${c.program_slug}`} data-testid={`my-course-${c.program_slug}`} className="block rounded-2xl border border-[#E5E5E7] bg-white p-5 hover:border-[#c9a227] hover:shadow-md transition-all">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#c9a227]">{c.tier_id}</span>
                        <span className="text-[10px] uppercase font-bold text-[#a8871a] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100">{c.status}</span>
                      </div>
                      <h3 className="text-[18px] font-extrabold text-[#1D1D1F] mb-1">{c.program_name}</h3>
                      <p className="text-[13px] text-[#6E6E73] mb-3">{c.schedule || 'Self-paced'}</p>
                      <div className="h-2 rounded-full bg-[#F5F5F7] overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#e6c35a] to-[#a8871a]" style={{ width: `${c.progress || 0}%` }} />
                      </div>
                      <div className="text-[11.5px] text-[#6E6E73] mt-1.5">Progress: {c.progress || 0}%{c.grade ? ` · Grade: ${c.grade}` : ''} · Open course →</div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {/* Browse all programs */}
            {otherPrograms.length > 0 && (
              <section data-testid="browse-all-programs">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-4 h-4 text-[#c9a227]" />
                  <h2 className="text-[15px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">Browse all programs</h2>
                  <span className="text-[11px] text-[#6E6E73]">{otherPrograms.length} more available</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {otherPrograms.map((p) => (
                    <Link key={p.slug} to={`/academy/programs/${p.slug}`} data-testid={`catalog-program-${p.slug}`}
                      className="block rounded-xl border border-[#E5E5E7] bg-white p-4 hover:border-[#c9a227] hover:shadow-sm transition-all">
                      <div className="flex items-start gap-2">
                        <LockIcon className="w-3.5 h-3.5 text-[#9ca3af] mt-1 shrink-0" />
                        <div>
                          <h3 className="text-[14.5px] font-extrabold text-[#1D1D1F] leading-tight mb-1">{p.name}</h3>
                          <p className="text-[12px] text-[#6E6E73] line-clamp-2">{p.description}</p>
                          <div className="text-[10.5px] text-[#c9a227] uppercase tracking-wider font-bold mt-2">View details & enrol →</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
